import { useState, useMemo, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '../../../services/api';
import { COMPONENT_QUERY, COMPONENT_QUERY_COMPARE } from '../../../constants/api';
import { getRegionQueryKey } from '../../../constants/utils';
import {
  configureRowsHandler,
  configureRowsRegionHandler,
} from '../services/overallPerformanceServices';
import { OverallPerformanceConstants } from '../constant/overallPerformanceConstant';

export function usePerformanceQueries() {
  // Table data state
  const [tableOneData, setTableOneData] = useState([]);
  const [tableTwoData, setTableTwoData] = useState([]);
  const [tableThreeData, setTableThreeData] = useState([]);

  // Table-specific error state
  const [tableOneError, setTableOneError] = useState(null);
  const [tableTwoError, setTableTwoError] = useState(null);
  const [tableThreeError, setTableThreeError] = useState(null);
  const initialLoadingState = useRef(true);

  // Memoize API functions to prevent recreation on each render
  const fetchComparisonData = useCallback(async (data) => {
    try {
      const component = data.spendToggleState
        ? OverallPerformanceConstants.LOCAL_COMPARE_PERIOD_TABLE_DATA
        : OverallPerformanceConstants.COMPARE_PERIOD_TABLE_DATA;

      const response = await api.post(`${COMPONENT_QUERY_COMPARE}?component=${component}`, {
        current_query_builder: {
          filters: data.currentFilters,
        },
        previous_query_builder: {
          filters: data.compareFilters,
        },
        join_columns: ['channel_name'],
      });

      return response?.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch comparison data');
    }
  }, []);

  const fetchYtdComparisonData = useCallback(async (data) => {
    try {
      const component = data.spendToggleState
        ? OverallPerformanceConstants.LOCAL_YTD_VIEW_TABLE_DATA
        : OverallPerformanceConstants.YTD_VIEW_TABLE_DATA;

      const response = await api.post(`${COMPONENT_QUERY_COMPARE}?component=${component}`, {
        current_query_builder: {
          filters: data.currentFilters,
        },
        previous_query_builder: {
          filters: data.compareFilters,
        },
        join_columns: ['store_id'],
      });

      return response?.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch YTD comparison data');
    }
  }, []);

  const fetchRegionCompData = useCallback(async (data) => {
    try {
      const component = getRegionQueryKey(data.spendToggleState);
      const response = await api.post(`${COMPONENT_QUERY}?component=${component}`, {
        [component]: {
          filters: data.filters,
        },
      });

      return response?.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch region comparison data');
    }
  }, []);

  const fetchRegionComparisonData = useCallback(async (data) => {
    try {
      const response = await api.post(
        `${COMPONENT_QUERY_COMPARE}?component=${OverallPerformanceConstants.REGION_COMPARE_PERIOD_TABLE_DATA}`,
        {
          current_query_builder: {
            filters: data.currentFilters,
          },
          previous_query_builder: {
            filters: data.compareFilters,
          },
          join_columns: ['region_id'],
        }
      );

      return response?.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch region period comparison data');
    }
  }, []);

  const fetchRegionYtdComparisonData = useCallback(async (data) => {
    try {
      const response = await api.post(
        `${COMPONENT_QUERY_COMPARE}?component=${OverallPerformanceConstants.REGION_YTD_VIEW_TABLE_DATA}`,
        {
          current_query_builder: {
            filters: data.currentFilters,
          },
          previous_query_builder: {
            filters: data.compareFilters,
          },
          join_columns: ['region_id'],
        }
      );

      return response?.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch region YTD comparison data');
    }
  }, []);

  // Table One - Period Comparison Query
  const { mutate: tableOneMutate, isLoading: isTableOneLoading } = useMutation({
    mutationFn: fetchComparisonData,
    onSuccess(data) {
      const result = data?.data?.map((storeData) => configureRowsHandler(storeData));

      if (result?.[0]?.rows) {
        setTableOneData(result?.[0]?.rows);
      }
      initialLoadingState.current = false;
      setTableOneError(null);
    },
    onError(error) {
      setTableOneError(error.message || 'Failed to load comparison data');
      initialLoadingState.current = false;
    },
  });

  // Table Two - YTD Comparison Query
  const { mutate: tableTwoMutate, isLoading: isTableTwoLoading } = useMutation({
    mutationFn: fetchYtdComparisonData,
    onSuccess(data) {
      const result = data?.data?.map((storeData) => configureRowsHandler(storeData));

      if (result?.[0]?.rows) {
        setTableTwoData(result?.[0]?.rows);
      }
      setTableTwoError(null);
    },
    onError(error) {
      setTableTwoError(error.message || 'Failed to load YTD comparison data');
    },
  });

  // Table Three - Region Comparison Query
  const { mutate: tableThreeMutate, isLoading: isTableThreeLoading } = useMutation({
    mutationFn: fetchRegionCompData,
    onSuccess(data, variables) {
      const component = getRegionQueryKey(variables.spendToggleState);

      if (data?.data) {
        const componentData = data?.data?.[component];

        if (componentData?.status === 'success' && componentData?.result) {
          const transformedData = componentData.result.map((item) =>
            configureRowsRegionHandler(item)
          );

          if (transformedData?.[0]?.rows) {
            setTableThreeData(transformedData[0].rows);
          }
          setTableThreeError(null);
        }
      }
    },
    onError(error) {
      console.error('Region comparison query error:', error);
      setTableThreeError(error.message || 'Failed to load region comparison data');
      setTableThreeData([]);
    },
  });

  // Region-specific queries
  const { mutate: regionTableOneMutate, isLoading: isRegionTableOneLoading } = useMutation({
    mutationFn: fetchRegionComparisonData,
    onSuccess(data) {
      const result = data?.data?.map((regionData) => configureRowsHandler(regionData));

      if (result?.[0]?.rows) {
        setTableOneData(result?.[0]?.rows);
      }
      setTableOneError(null);
    },
    onError(error) {
      setTableOneError(error.message || 'Failed to load YTD comparison data region data');
    },
  });

  const { mutate: regionTableTwoMutate, isLoading: isRegionTableTwoLoading } = useMutation({
    mutationFn: fetchRegionYtdComparisonData,
    onSuccess(data) {
      const result = data?.data?.map((regionData) => configureRowsHandler(regionData));

      if (result?.[0]?.rows) {
        setTableTwoData(result?.[0]?.rows);
      }
      setTableTwoError(null);
    },
    onError(error) {
      setTableTwoError(error.message || 'Failed to load YTD comparison data region data');
    },
  });

  // Combined loading state
  const isAnyQueryLoading = useMemo(
    () =>
      initialLoadingState.current ||
      isTableOneLoading ||
      isTableTwoLoading ||
      isTableThreeLoading ||
      isRegionTableOneLoading ||
      isRegionTableTwoLoading,
    [
      isTableOneLoading,
      isTableTwoLoading,
      isTableThreeLoading,
      isRegionTableOneLoading,
      isRegionTableTwoLoading,
    ]
  );

  return {
    // Table One
    tableOneData,
    fetchTableOne: tableOneMutate,
    isTableOneLoading,
    tableOneError,

    // Table Two
    tableTwoData,
    fetchTableTwo: tableTwoMutate,
    isTableTwoLoading,
    tableTwoError,

    // Table Three
    tableThreeData,
    fetchTableThree: tableThreeMutate,
    isTableThreeLoading,
    tableThreeError,

    // Region-specific
    fetchRegionTableOne: regionTableOneMutate,

    fetchRegionTableTwo: regionTableTwoMutate,

    // Combined state
    isAnyQueryLoading,
  };
}
